import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Menu, X, Construction, BrainCircuit } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import AuditLog from "@/components/admin/AuditLog";
import LlmObservability from "@/components/observability/LlmObservability";

const observabilityItems = [
  { id: "audit-log", label: "Audit Log", icon: FileText },
  { id: "llm-observability", label: "LLM Observability", icon: BrainCircuit },
];

const Observability = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(() => {
    const fromUrl = searchParams.get("tab");
    return observabilityItems.find((i) => i.id === fromUrl) ? fromUrl! : observabilityItems[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const activeItem = observabilityItems.find((i) => i.id === active);

  const selectTab = (id: string) => {
    setActive(id);
    setSidebarOpen(false);
    setSearchParams((prev) => { prev.set("tab", id); return prev; });
  };

  return (
    <div className="flex flex-1 min-h-0 relative overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 transition-transform duration-300",
          "md:relative md:inset-auto md:z-auto md:translate-x-0 md:flex md:shrink-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <aside className="flex h-full w-56 flex-col border-r border-border bg-sidebar">
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data Observability</h2>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <nav className="flex-1 space-y-1 px-2">
            {observabilityItems.map((item) => (
              <button
                key={item.id}
                onClick={() => selectTab(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active === item.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile toolbar */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 md:hidden shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="text-sm font-medium">{activeItem?.label}</span>
        </div>

        {active === "audit-log" ? (
          <AuditLog />
        ) : active === "llm-observability" ? (
          <LlmObservability />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <Construction className="h-8 w-8 text-accent-foreground" />
            </div>
            <h1 className="text-2xl font-semibold">{activeItem?.label}</h1>
            <p className="max-w-md text-center text-muted-foreground">
              This feature is coming soon. Check back later for updates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Observability;
